import React, { useCallback, useMemo, useState } from "react";
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useFetchOrganizationsQuery,
  useFetchOrganizationUsersQuery,
} from "../../services/profileService";
import { useCopyRoleReviewMutation } from "../../services/roleReviewService";

const ROLE_TYPE = {
  managers: "manager",
  employees: "employee",
};

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getUserDisplayName = (user) => {
  if (!user || typeof user !== "object") return "—";
  if (user.name) return String(user.name);
  const parts = [user.first_name, user.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (user.full_name) return String(user.full_name);
  return "—";
};

const CopyVersionModal = ({ open, onClose, onSuccess, sourceUserId, sourceUserName, reportVersionId }) => {
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [activeRoleType, setActiveRoleType] = useState(ROLE_TYPE.managers);
  const [errorMessage, setErrorMessage] = useState("");
  const [assigningUserId, setAssigningUserId] = useState(null);

  const { data: orgsPayload, isLoading: orgsLoading } =
    useFetchOrganizationsQuery(undefined, { skip: !open });

  const organizations = useMemo(() => normalizeArray(orgsPayload), [orgsPayload]);

  const {
    data: usersPayload,
    isLoading: usersLoading,
    isFetching: usersFetching,
  } = useFetchOrganizationUsersQuery(
    { organizationId: selectedOrgId, roleType: activeRoleType },
    { skip: !selectedOrgId }
  );

  const users = useMemo(() => normalizeArray(usersPayload), [usersPayload]);

  const rows = useMemo(
    () =>
      users.map((member, index) => ({
        id: member.id ?? member.user_id ?? `member-${index}`,
        name: getUserDisplayName(member),
        email: member.email ?? "—",
        role:
          member.role_type ??
          member.role ??
          (activeRoleType === ROLE_TYPE.managers ? "manager" : "employee"),
      })),
    [users, activeRoleType]
  );

  const [copyRoleReview] = useCopyRoleReviewMutation();

  const handleTabChange = useCallback((_event, newRoleType) => {
    setActiveRoleType(newRoleType);
    setErrorMessage("");
  }, []);

  const handleOrgChange = useCallback((event) => {
    setSelectedOrgId(event.target.value);
    setErrorMessage("");
  }, []);

  const resetState = useCallback(() => {
    setSelectedOrgId("");
    setActiveRoleType(ROLE_TYPE.managers);
    setErrorMessage("");
    setAssigningUserId(null);
  }, []);

  const handleAssign = useCallback(
    async (targetUserId, targetUserName) => {
      setAssigningUserId(targetUserId);
      setErrorMessage("");
      try {
        const payload = {
          source_user_id: sourceUserId,
          target_user_id: targetUserId,
          report_version_id: reportVersionId,
        };
        if (selectedOrgId) {
          payload.target_organization_id = Number(selectedOrgId);
        }
        await copyRoleReview(payload).unwrap();
        // Close modal first, then surface the success message in parent
        resetState();
        onClose();
        onSuccess?.(`Role review copied successfully to ${targetUserName}.`);
      } catch (err) {
        const msg =
          err?.data?.message ??
          err?.data?.detail ??
          err?.message ??
          "Failed to copy role review. Please try again.";
        setErrorMessage(String(msg));
      } finally {
        setAssigningUserId(null);
      }
    },
    [copyRoleReview, sourceUserId, selectedOrgId, reportVersionId, resetState, onClose, onSuccess]
  );

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const tabLabel =
    activeRoleType === ROLE_TYPE.managers ? "Managers" : "Employees";

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", minWidth: 70 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
      {
        field: "action",
        headerName: "Action",
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          const isSelf = params.row.id === sourceUserId;
          const isThisAssigning = assigningUserId === params.row.id;
          return (
            <Tooltip title={isSelf ? "Cannot assign to the source user" : "Assign this version"}>
              <span>
                <AssignBtn
                  disabled={isSelf || assigningUserId !== null}
                  onClick={() => handleAssign(params.row.id, params.row.name)}
                >
                  {isThisAssigning ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <>
                      <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Assign
                    </>
                  )}
                </AssignBtn>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [handleAssign, sourceUserId, assigningUserId]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-paper": { borderRadius: "10px" } }}
    >
      <ModalTitleWrap>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#000" }}>
            Copy Role Review
          </Typography>
          {sourceUserName && (
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
              Source: {sourceUserName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close modal">
          <CloseIcon />
        </IconButton>
      </ModalTitleWrap>

      <DialogContent sx={{ p: 3 }}>
        {/* Inline error banner — success is handled via Snackbar in the parent */}
        {errorMessage && (
          <MuiAlert
            severity="error"
            sx={{ mb: 2, borderRadius: "7px" }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </MuiAlert>
        )}

        {/* Organization select */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="org-select-label">Select Organization</InputLabel>
          <OrgSelect
            labelId="org-select-label"
            value={selectedOrgId}
            label="Select Organization"
            onChange={handleOrgChange}
            disabled={orgsLoading}
            displayEmpty
          >
            {orgsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            ) : organizations.length === 0 ? (
              <MenuItem disabled>No organizations found</MenuItem>
            ) : (
              organizations.map((org) => {
                const orgId = org.id ?? org.organization_id ?? org.organizationID;
                const orgName =
                  org.organization_name ?? org.name ?? org.organizationName;
                return (
                  <MenuItem key={orgId} value={orgId}>
                    {orgName ?? `Organization ${orgId}`}
                  </MenuItem>
                );
              })
            )}
          </OrgSelect>
        </FormControl>

        {/* Role tabs + user list */}
        {selectedOrgId && (
          <>
            <TabsWrap
              value={activeRoleType}
              onChange={handleTabChange}
              aria-label="Select role type"
              sx={{ mb: 2 }}
            >
              <Tab label="Managers" value={ROLE_TYPE.managers} />
              <Tab label="Employees" value={ROLE_TYPE.employees} />
            </TabsWrap>

            {usersLoading || usersFetching ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  py: 4,
                }}
              >
                <CircularProgress size={28} />
                <Typography color="text.secondary">
                  Loading {tabLabel.toLowerCase()}…
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 30]}
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                localeText={{
                  noRowsLabel: `No ${tabLabel.toLowerCase()} found for this organization`,
                }}
                sx={{
                  "& .MuiDataGrid-iconButtonContainer": {
                    visibility: "visible !important",
                    width: "auto !important",
                    svg: { opacity: "1 !important" },
                  },
                }}
              />
            )}
          </>
        )}

        {!selectedOrgId && !orgsLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Please select an organization to view its managers and employees.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CopyVersionModal;

const ModalTitleWrap = styled(DialogTitle)(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: "20px 24px 16px",
  borderBottom: "1px solid #E5E7EB",
}));

const OrgSelect = styled(Select)(() => ({
  borderRadius: "7px",
  background: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DADADA",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1085AB",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1085AB",
  },
}));

const TabsWrap = styled(Tabs)(() => ({
  minHeight: "42px",
  borderBottom: "1px solid #E5E7EB",
  "& .MuiTab-root": {
    textTransform: "capitalize",
    fontSize: "16px",
    fontWeight: "500",
    color: "#6B7280",
    minHeight: "42px",
  },
  "& .Mui-selected": {
    color: "#1085AB !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#1085AB",
  },
}));

const AssignBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "13px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#1085AB",
  borderColor: "#1085AB",
  borderRadius: "5px",
  padding: "7px 12px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#0c6d8e",
    borderColor: "#0c6d8e",
  },
  "&.Mui-disabled": {
    background: "#B0BEC5",
    color: "#ffffff",
  },
}));
